import { useEffect, useRef, useState } from "react";
import { Box, Chip, List, ListItemButton, ListItemText, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DELTA_ACCENT, PAPER_GREY } from "../colors";
import { ProPill } from "./ProPill";
import { useAuth } from "../../auth/AuthContext";

export type NavItem = {
  value: string;
  label: string;
  pro?: boolean;
};

export const SectionNav = (props: {
  items: NavItem[];
  value: string;
  onChange: (value: string) => void;
}) => {
  const { items, value, onChange } = props;
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateFades = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 0);
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateFades();
    // isMobile starts false on first render (useMediaQuery's SSR-safe default)
    // and flips true right after mount - that's the render where scrollRef
    // actually attaches to the DOM, so it has to be a dependency here too,
    // not just items, or this never re-measures once we're actually mobile.
  }, [items, isMobile]);

  if (isMobile) {
    return (
      <Box style={{ position: "relative" }}>
        <Box
          ref={scrollRef}
          onScroll={updateFades}
          style={{ display: "flex", overflowX: "auto", gap: 8, paddingBottom: 4 }}
        >
          {items.map((item) => (
            <Chip
              key={item.value}
              label={
                item.pro && !auth.isPro ? (
                  <Box style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {item.label} <ProPill />
                  </Box>
                ) : (
                  item.label
                )
              }
              onClick={() => onChange(item.value)}
              color={value === item.value ? "primary" : undefined}
              variant={value === item.value ? "filled" : "outlined"}
              style={{ flexShrink: 0 }}
            />
          ))}
        </Box>
        {showLeftFade && (
          <Box
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 4,
              width: 24,
              pointerEvents: "none",
              background: `linear-gradient(to right, ${PAPER_GREY}, rgba(245,245,247,0))`,
            }}
          />
        )}
        {showRightFade && (
          <Box
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 4,
              width: 24,
              pointerEvents: "none",
              background: `linear-gradient(to left, ${PAPER_GREY}, rgba(245,245,247,0))`,
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <List style={{ width: 260, flexShrink: 0, padding: 8 }}>
      {items.map((item) => {
        const selected = value === item.value;
        return (
          <ListItemButton
            key={item.value}
            selected={selected}
            onClick={() => onChange(item.value)}
            sx={{
              borderLeft: selected ? `4px solid ${DELTA_ACCENT}` : "4px solid transparent",
              backgroundColor: selected ? "rgba(142, 59, 147, 0.10)" : "transparent",
              borderRadius: "8px",
              marginBottom: "4px",
              "&:hover": {
                backgroundColor: selected ? "rgba(142, 59, 147, 0.16)" : "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                style: {
                  fontWeight: selected ? 700 : 500,
                  color: selected ? DELTA_ACCENT : undefined,
                },
              }}
            />
            {item.pro && !auth.isPro && <ProPill />}
          </ListItemButton>
        );
      })}
    </List>
  );
};

export default SectionNav;
